import { useCallback, useEffect, useState } from "react";
import { getFileDownloadUrl } from "../../../api/firebase/firestoreService";
import { useDataAccumulatorForSingleOrder } from "../../../context/dataAccumulatorForSingleOrder";
import { logDataToConsole } from "../../../utils/customLogger";

/**
 * Manages attachment download URLs for the current medical PA order.
 *
 * Automatically fetches the clinical document URL when the accumulator order's
 * clinical document path changes. Additional URLs can be fetched on demand via
 * `fetchDownloadUrl`.
 *
 * @returns {object} Hook state and helpers
 * @returns {string[]} urls - Resolved download URLs accumulated so far.
 * @returns {boolean} isLoading - `true` while a fetch is in progress.
 * @returns {(refUrl: string) => Promise<void>} fetchDownloadUrl - Fetches a
 *   Firebase Storage download URL for the given reference path and appends it
 *   to `urls`.
 */
export const useAttachmentUrls = () => {
  const { generalOrderDetails } = useDataAccumulatorForSingleOrder();
  const order = generalOrderDetails?.order ?? null;
  const [urls, setUrls] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const fetchDownloadUrl = useCallback(async (refUrl: string) => {
    setIsLoading(true);
    try {
      const downloadUrl = await getFileDownloadUrl(refUrl);
      setUrls((prev) =>
        prev.includes(downloadUrl) ? prev : [...prev, downloadUrl],
      );
    } catch (err) {
      logDataToConsole("Error fetching download url", err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    setUrls([]);
    if (order?.clinical_document?.file_path) {
      fetchDownloadUrl(order.clinical_document.file_path);
    }
  }, [order?.clinical_document?.file_path, fetchDownloadUrl]);

  return { urls, isLoading, fetchDownloadUrl };
};
